// frontend/shared-ui/src/forms/PricingCalculator.jsx
import React, { useMemo, useEffect, useState, useRef } from "react";
import { formatMoney, formatPercent, computeMargin } from "../utils/money";
import { useShipmentContext } from "./useShipmentContext";

export default function PricingCalculator({
  form,
  onChange,
  attributes = {},
  costDefaults = null,
}) {
  const { fullPerCbm, context } = useShipmentContext();

  // ── Package dims (from attributes) ────────────────────────
  const pkgL = Number(attributes.packageLength_cm) || 0;
  const pkgW = Number(attributes.packageWidth_cm) || 0;
  const pkgH = Number(attributes.packageHeight_cm) || 0;
  const pkgUnits = Number(attributes.unitsPerPackage) || 0;

  const legacyCbm = Number(form.volumeCbm) || 0;
  const hasPackageData = pkgL > 0 && pkgW > 0 && pkgH > 0 && pkgUnits > 0;
  const [usePackage, setUsePackage] = useState(hasPackageData);

  // Reset toggle when the product changes
  useEffect(() => {
    setUsePackage(hasPackageData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, form.sku]);

  // ── CBM per unit ──────────────────────────────────────────
  const cbmPerUnit = useMemo(() => {
    if (usePackage) {
      if (pkgL > 0 && pkgW > 0 && pkgH > 0 && pkgUnits > 0) {
        const pkgCbm = (pkgL * pkgW * pkgH) / 1000000;
        return pkgCbm / pkgUnits;
      }
      return 0;
    }
    return legacyCbm;
  }, [usePackage, pkgL, pkgW, pkgH, pkgUnits, legacyCbm]);

  // ── Cost components ───────────────────────────────────────
  const factoryCost = Number(form.costPrice) || 0;
  const actualPrice = Number(form.price) || 0;
  const targetMarginPct = Number(context.targetMarginPct) || 30;

  const shipmentCost = cbmPerUnit > 0 ? cbmPerUnit * fullPerCbm : 0;
  const computedTotalCost = factoryCost + shipmentCost;

  // Fall back to the DB value if the calculator has no CBM data to work with.
  // This preserves the saved totalCost when editing an existing product
  // whose attributes haven't been re-entered.
  const totalCost =
    cbmPerUnit > 0 ? computedTotalCost : Number(form.totalCost) || factoryCost;

  // Suggested price from total cost
  const marginFraction = Math.min(Math.max(targetMarginPct, 0), 95) / 100;
  const suggestedPrice =
    marginFraction < 1 ? totalCost / (1 - marginFraction) : totalCost * 2;

  const realMargin = computeMargin(actualPrice, totalCost);

  // Auto-fill price when empty
  useEffect(() => {
    if (suggestedPrice > 0 && !form.price) {
      onChange("price", Number(suggestedPrice.toFixed(2)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedPrice]);

  // ═══════════════════════════════════════════════════════════
  // Mount guard: don't emit derived values until the user makes
  // a change. Otherwise we overwrite the freshly-seeded DB values
  // on modal open (this was the "totalCost resets on edit" bug).
  // ═══════════════════════════════════════════════════════════
  const mountedRef = useRef(false);
  useEffect(() => {
    // Mark as "mounted" after the first paint so subsequent effect
    // runs (triggered by user interaction) are allowed to emit.
    const t = setTimeout(() => {
      mountedRef.current = true;
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, form.sku]);

  // ── Emit volumeCbm — only after mount ─────────────────────
  const lastEmittedCbm = useRef(null);
  useEffect(() => {
    if (!mountedRef.current) return;
    const rounded = Number(cbmPerUnit.toFixed(6));
    if (lastEmittedCbm.current !== rounded) {
      lastEmittedCbm.current = rounded;
      const currentCbm = Number(form.volumeCbm) || 0;
      if (Math.abs(currentCbm - rounded) > 1e-9) {
        onChange("volumeCbm", rounded);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbmPerUnit, form.id]);

  // ── Emit totalCost — only after mount ─────────────────────
  const lastEmittedTotal = useRef(null);
  useEffect(() => {
    if (!mountedRef.current) return;
    const rounded = Number(totalCost.toFixed(2));
    if (lastEmittedTotal.current !== rounded) {
      lastEmittedTotal.current = rounded;
      onChange("totalCost", rounded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCost, form.id]);

  // ── Emit shipmentRate — only after mount ──────────────────
  const lastEmittedRate = useRef(null);
  useEffect(() => {
    if (!mountedRef.current) return;
    const rounded = Number((fullPerCbm || 0).toFixed(2));
    if (lastEmittedRate.current !== rounded) {
      lastEmittedRate.current = rounded;
      onChange("shipmentRate", rounded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPerCbm, form.id]);

  // ── Auto-seed CBM from costDefaults for new products ──────
  useEffect(() => {
    if (
      costDefaults?.avgVolumeCbm > 0 &&
      !form.id &&
      !form.volumeCbm &&
      !attributes.packageLength_cm &&
      !usePackage
    ) {
      onChange("volumeCbm", Number(costDefaults.avgVolumeCbm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costDefaults?.avgVolumeCbm, form.id]);

  const hasCbm = cbmPerUnit > 0;
  const hasFreight = fullPerCbm > 0;

  const setAttr = (key, value) => onChange(`attributes.${key}`, value);

  const [localPkg, setLocalPkg] = useState({
    l: attributes.packageLength_cm ?? "",
    w: attributes.packageWidth_cm ?? "",
    h: attributes.packageHeight_cm ?? "",
    units: attributes.unitsPerPackage ?? "",
    cbm: form.volumeCbm ?? "",
  });

  useEffect(() => {
    setLocalPkg({
      l: attributes.packageLength_cm ?? "",
      w: attributes.packageWidth_cm ?? "",
      h: attributes.packageHeight_cm ?? "",
      units: attributes.unitsPerPackage ?? "",
      cbm: form.volumeCbm ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, form.sku]);

  const updatePkgField = (key, raw) => {
    setLocalPkg((s) => ({ ...s, [key]: raw }));
    const num = raw === "" ? 0 : Number(raw);
    if (key === "cbm") {
      onChange("volumeCbm", Number.isFinite(num) ? num : 0);
    } else {
      const map = {
        l: "packageLength_cm",
        w: "packageWidth_cm",
        h: "packageHeight_cm",
        units: "unitsPerPackage",
      };
      setAttr(map[key], Number.isFinite(num) ? num : 0);
    }
  };

  return (
    <div className="fh-pricing-calc">
      <div className="fh-pricing-calc__grid fh-pricing-calc__grid--3">
        <div className="fh-form-field">
          <label className="fh-form-field__label">
            Factory Cost (FOB){" "}
            <span className="fh-form-field__unit">per unit</span>
          </label>
          <input
            className="fh-input"
            type="number"
            step="0.0001"
            value={form.costPrice ?? ""}
            onChange={(e) => onChange("costPrice", Number(e.target.value) || 0)}
            placeholder="0.32"
          />
          <div className="fh-form-field__hint">Supplier unit cost</div>
        </div>

        <div className="fh-form-field">
          <label className="fh-form-field__label">
            Total Cost{" "}
            <span className="fh-form-field__unit">landed per unit</span>
          </label>
          <input
            className="fh-input fh-pricing-calc__readonly"
            type="text"
            value={formatMoney(totalCost, { symbol: "$", decimals: 4 })}
            readOnly
            tabIndex={-1}
          />
          <div className="fh-form-field__hint">
            {cbmPerUnit > 0 ? (
              <>
                {" "}
                = FOB + shipment (
                {formatMoney(shipmentCost, { symbol: "$", decimals: 4 })})
              </>
            ) : (
              <> = saved landed cost (no CBM entered)</>
            )}
          </div>
        </div>

        <div className="fh-form-field">
          <label className="fh-form-field__label">Selling Price</label>
          <input
            className="fh-input"
            type="number"
            step="0.01"
            value={form.price ?? ""}
            onChange={(e) => onChange("price", Number(e.target.value) || 0)}
            placeholder={suggestedPrice ? suggestedPrice.toFixed(2) : "0.00"}
          />
          <div className="fh-form-field__hint">
            Suggested:{" "}
            {formatMoney(suggestedPrice, { symbol: "$", decimals: 2 })}
          </div>
        </div>
      </div>

      <div className="fh-pricing-calc__section">
        <div className="fh-pricing-calc__section-title">
          Shipment space per unit
          <span className="fh-pricing-calc__badge">
            {usePackage ? "Package dimensions" : "Direct CBM"}
          </span>
        </div>

        <div className="fh-pricing-calc__toggle-row">
          <label className="fh-checkbox">
            <input
              type="checkbox"
              checked={usePackage}
              onChange={(e) => {
                const next = e.target.checked;
                setUsePackage(next);
                if (next) {
                  onChange("volumeCbm", 0);
                  setLocalPkg((s) => ({ ...s, cbm: "" }));
                } else {
                  setAttr("packageLength_cm", 0);
                  setAttr("packageWidth_cm", 0);
                  setAttr("packageHeight_cm", 0);
                  setAttr("unitsPerPackage", 0);
                  setLocalPkg((s) => ({
                    ...s,
                    l: "",
                    w: "",
                    h: "",
                    units: "",
                  }));
                }
                // Enable emitting after any manual toggle
                mountedRef.current = true;
              }}
            />
            <span>I know the package size (recommended)</span>
          </label>
        </div>

        {usePackage ? (
          <div className="fh-pricing-calc__grid fh-pricing-calc__grid--5">
            <div className="fh-form-field">
              <label className="fh-form-field__label">Length</label>
              <input
                className="fh-input"
                type="number"
                step="0.1"
                value={localPkg.l}
                onChange={(e) => updatePkgField("l", e.target.value)}
                placeholder="40"
              />
              <div className="fh-form-field__hint">cm</div>
            </div>
            <div className="fh-form-field">
              <label className="fh-form-field__label">Width</label>
              <input
                className="fh-input"
                type="number"
                step="0.1"
                value={localPkg.w}
                onChange={(e) => updatePkgField("w", e.target.value)}
                placeholder="30"
              />
              <div className="fh-form-field__hint">cm</div>
            </div>
            <div className="fh-form-field">
              <label className="fh-form-field__label">Height</label>
              <input
                className="fh-input"
                type="number"
                step="0.1"
                value={localPkg.h}
                onChange={(e) => updatePkgField("h", e.target.value)}
                placeholder="20"
              />
              <div className="fh-form-field__hint">cm</div>
            </div>
            <div className="fh-form-field">
              <label className="fh-form-field__label">Units per box</label>
              <input
                className="fh-input"
                type="number"
                step="1"
                value={localPkg.units}
                onChange={(e) => updatePkgField("units", e.target.value)}
                placeholder="500"
              />
              <div className="fh-form-field__hint">qty in one package</div>
            </div>
            <div className="fh-form-field">
              <label className="fh-form-field__label">CBM / unit</label>
              <input
                className="fh-input"
                type="text"
                value={hasCbm ? cbmPerUnit.toFixed(6) : ""}
                disabled
                placeholder="auto"
              />
              <div className="fh-form-field__hint">
                {pkgL > 0 && pkgW > 0 && pkgH > 0 && pkgUnits > 0
                  ? `${((pkgL * pkgW * pkgH) / 1000000).toFixed(4)} CBM ÷ ${pkgUnits} = ${cbmPerUnit.toFixed(6)}`
                  : "fill package size"}
              </div>
            </div>
          </div>
        ) : (
          <div className="fh-pricing-calc__grid fh-pricing-calc__grid--1">
            <div className="fh-form-field">
              <label className="fh-form-field__label">CBM per unit</label>
              <input
                className="fh-input"
                type="number"
                step="0.000001"
                value={localPkg.cbm}
                onChange={(e) => updatePkgField("cbm", e.target.value)}
                placeholder={costDefaults?.avgVolumeCbm || "0.0024"}
              />
              <div className="fh-form-field__hint">
                Space one unit occupies, in CBM
                {costDefaults?.note && (
                  <span
                    style={{
                      display: "block",
                      marginTop: 2,
                      color: "#6b7280",
                    }}
                  >
                    💡 {costDefaults.note}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="fh-pricing-calc__summary">
        <div className="fh-pricing-calc__row">
          <span className="text-muted">Factory cost (FOB)</span>
          <span>{formatMoney(factoryCost, { symbol: "$", decimals: 4 })}</span>
        </div>
        <div className="fh-pricing-calc__row">
          <span className="text-muted">
            + Shipment cost
            {hasCbm && (
              <span style={{ fontSize: 11 }}>
                {" "}
                ({cbmPerUnit.toFixed(6)} CBM ×{" "}
                {formatMoney(fullPerCbm, { symbol: "$", decimals: 2 })}/CBM)
              </span>
            )}
          </span>
          <span>{formatMoney(shipmentCost, { symbol: "$", decimals: 4 })}</span>
        </div>
        <div className="fh-pricing-calc__row is-landed">
          <span>Total cost (landed)</span>
          <strong>
            {formatMoney(totalCost, { symbol: "$", decimals: 4 })}
          </strong>
        </div>
        <div className="fh-pricing-calc__row">
          <span className="text-muted">
            Suggested price (at {targetMarginPct}% margin on total cost)
          </span>
          <span>
            {formatMoney(suggestedPrice, { symbol: "$", decimals: 2 })}
          </span>
        </div>
        <div
          className={`fh-pricing-calc__row is-margin ${
            realMargin === null
              ? ""
              : realMargin < 0
                ? "is-loss"
                : realMargin < 10
                  ? "is-thin"
                  : realMargin < 30
                    ? "is-okay"
                    : "is-healthy"
          }`}
        >
          <span>Real margin at your price</span>
          <strong>
            {realMargin === null ? "—" : formatPercent(realMargin)}
          </strong>
        </div>
      </div>

      {!hasFreight && (
        <div className="fh-pricing-calc__warning">
          ⚠ Shipment costs aren't set. Suggested price is based on FOB only.
          Expand <em>Shipment costs</em> above to include freight.
        </div>
      )}
      {hasFreight && !hasCbm && (
        <div className="fh-pricing-calc__warning">
          ⚠ No CBM entered for this product. Total cost uses the saved value.
        </div>
      )}
      {shipmentCost > factoryCost && factoryCost > 0 && (
        <div className="fh-pricing-calc__warning">
          ⚠ Shipment cost (
          <strong>
            {formatMoney(shipmentCost, { symbol: "$", decimals: 4 })}
          </strong>
          ) is higher than factory cost (
          <strong>
            {formatMoney(factoryCost, { symbol: "$", decimals: 4 })}
          </strong>
          ). Check package dims and units per box.
        </div>
      )}
    </div>
  );
}
